import { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDocumentHead } from '@/hooks/use-document-head'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { Button } from '@/components/ui/button'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Form } from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import {
  agentsApiClient,
  type AgentWithVoiceSettings,
} from '@/lib/api/agents.client'
import { agentsAdminApiClient } from '@/lib/api/agents-admin.client'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { workflowsApiClient } from '@/lib/api/workflows.client'
import { triggersApiClient, scenariosApiClient } from '@/lib/api'
import { toast } from 'sonner'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useHeaderExtra, useHeaderRight } from '@/contexts/header.context'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { FeatureFlags } from '@/types/feature-flags'
import {
  isDialOutAgent,
  DIAL_OUT_INTENT_DEFAULT,
  VOICE_SETTINGS_DEFAULTS,
  VOICE_OPTIONS,
  WEBHOOK_EVENT_TO_TRIGGER,
  getTemplateByScenario,
  extractVariablesFromTexts,
  getScenarioVariableNames,
  type AgentChannel,
  type AgentDirection,
  type AgentTemplate,
} from '@super/shared-types'
import type {
  AgentScenario,
  KnowledgeBase,
  WorkflowWithActionGroups,
} from '@super/shared-types'
import type { AgentTrigger, Scenario } from '@super/shared-types'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Braces, PhoneOutgoing } from 'lucide-react'
import {
  formSchema,
  getDefaultValues,
  normalizeTriggerForApi,
  resolveFormTriggerType,
  buildDialOutSettingsPayload,
  intakeFormValuesFromConfig,
  type FormValues,
  DIRECTION_CONFIG,
  AgentCard,
  TriggerRow,
  ChannelSettingsCard,
  ResourcesCard,
  SmsFollowUpCard,
  AgentAdvancedSettingsCard,
  ToolsCard,
  DialOutSettingsCard,
  AgentDetailSaveIsland,
  VariablesSidePanel,
} from '@/features/agent-detail'
import { IntakeFormCard } from '@/features/agent-detail/IntakeFormCard'
import { SimulateCallModal } from '@/features/outbound-call'

export function AgentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()

  const isNewAgent = !id
  const typeParam = searchParams.get('type')
  const templateParam = searchParams.get('template')
  const presetTriggerEventName = searchParams.get('triggerEventName')
  const initialDirection: AgentDirection =
    typeParam === 'INBOUND' || typeParam === 'OUTBOUND' ? typeParam : 'INBOUND'
  const initialTemplate = useMemo((): AgentTemplate | undefined => {
    if (!templateParam || !isNewAgent) return undefined
    return getTemplateByScenario(templateParam as AgentScenario)
  }, [templateParam, isNewAgent])

  useDocumentHead({
    title: isNewAgent ? 'New Agent' : 'Agent Settings',
    description: 'Configure your AI agent',
  })

  const [showSimulateModal, setShowSimulateModal] = useState(false)
  const [loading, setLoading] = useState(!isNewAgent)
  const [saving, setSaving] = useState(false)
  const [agent, setAgent] = useState<AgentWithVoiceSettings | null>(null)
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([])
  const [workflows, setWorkflows] = useState<WorkflowWithActionGroups[]>([])
  const [triggers, setTriggers] = useState<AgentTrigger[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [allAgents, setAllAgents] = useState<AgentWithVoiceSettings[]>([])
  const [connectedIntegrationTypes, setConnectedIntegrationTypes] = useState<
    string[]
  >([])
  const smsFollowUpInputRef = useRef<HTMLTextAreaElement>(null)
  const declinedMessageInputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null)
  const [variablesPanelOpen, setVariablesPanelOpen] = useState(true)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(initialDirection, initialTemplate),
  })

  const watchedDirection = form.watch('direction')
  const watchedChannels = form.watch('channels')
  const watchedTriggerType = form.watch('triggerType')
  const watchedScenarioId = form.watch('scenarioId')
  const watchedTriggerId = form.watch('triggerId')
  // Variables declared on the selected custom Trigger (from the dropdown payload).
  const selectedTriggerVariables = watchedTriggerId
    ? (triggers.find(t => t.id === watchedTriggerId)?.variables ?? [])
    : []
  const directionForDisplay =
    watchedDirection === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND'
  const { icon: DirectionIcon, label: directionLabel } =
    DIRECTION_CONFIG[directionForDisplay]
  const isReceptionist =
    directionForDisplay === 'INBOUND' &&
    (!watchedTriggerType || watchedTriggerType === 'INBOUND_CALL')

  const { isEnabled } = useFeatureFlag()
  // Compute inbound trigger constraints from all agents (exclude current agent)
  const usedScenarioIds = useMemo(() => {
    const ids = new Set<string>()
    for (const agent of allAgents) {
      if (agent.id === id || agent.direction !== 'INBOUND') continue
      for (const trigger of agent.agentTriggers ?? []) {
        if (trigger.type === 'SCENARIO' && trigger.scenarioId)
          ids.add(trigger.scenarioId)
      }
    }
    return ids
  }, [allAgents, id])

  // Webhook triggers already claimed by another active agent that shares a
  // channel with this one — one active agent per (trigger, channel).
  const usedTriggerEventNames = useMemo(() => {
    const currentChannels = watchedChannels ?? []
    const names = new Set<string>()
    if (currentChannels.length === 0) return names
    for (const agent of allAgents) {
      if (agent.id === id || agent.active === false) continue
      const overlaps = (agent.channels ?? []).some(channel =>
        currentChannels.includes(channel),
      )
      if (!overlaps) continue
      for (const trigger of agent.agentTriggers ?? []) {
        if (trigger.type === 'WEBHOOK' && trigger.eventName)
          names.add(trigger.eventName)
      }
    }
    return names
  }, [allAgents, id, watchedChannels])

  const currentDefaultAgentName = useMemo(() => {
    const defaultAgent = allAgents.find(agent => {
      if (agent.id === id || agent.direction !== 'INBOUND' || !agent.active)
        return false
      const triggerType = agent.voiceSettings?.triggerType
      return !triggerType || triggerType === 'INBOUND_CALL'
    })
    return defaultAgent?.name ?? null
  }, [allAgents, id])

  const [togglingActive, setTogglingActive] = useState(false)

  const handleToggleActive = async () => {
    if (!agent || !currentCompany?.id) return
    const newActive = !agent.active
    try {
      setTogglingActive(true)
      const updated = isSuperAdmin
        ? await agentsAdminApiClient.updateAgentForCompany(agent.id, {
            active: newActive,
          })
        : await agentsApiClient.updateAgent(agent.id, { active: newActive })

      if (updated) {
        setAgent(updated)
        toast.success(`Agent ${newActive ? 'activated' : 'deactivated'}`)
      }
    } catch (error) {
      console.error('Error toggling agent status:', error)
      toast.error('Failed to update agent status')
    } finally {
      setTogglingActive(false)
    }
  }

  useHeaderExtra(
    <div className="flex items-center">
      <Link
        to="/agents"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
      >
        <ArrowLeft className="w-4 h-4 text-muted-foreground" />
      </Link>
      <div className="ml-4 flex h-8 items-center">
        <span className="flex h-8 items-center gap-1.5 text-sm leading-none text-muted-foreground">
          <DirectionIcon className="h-3.5 w-3.5 shrink-0 [&_svg]:block" />
          {directionLabel}
        </span>
      </div>
      {!isNewAgent && agent && (
        <div className="ml-4 flex items-center gap-2 border-l pl-4">
          <Switch
            checked={agent.active !== false}
            onCheckedChange={handleToggleActive}
            disabled={togglingActive}
            aria-label="Toggle agent active status"
          />
          <Badge
            variant={agent.active !== false ? 'default' : 'secondary'}
            className={
              agent.active !== false
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs'
                : 'bg-muted text-muted-foreground text-xs'
            }
          >
            {agent.active !== false ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      )}
    </div>,
    [watchedDirection, agent?.active, isNewAgent, togglingActive],
  )

  const outboundEnabled = isEnabled(FeatureFlags.OUTBOUND)
  const inboundMultiAgentEnabled = isEnabled(FeatureFlags.INBOUND_MULTI_AGENT)
  const ownerLeadMeetingSkillEnabled = isEnabled(
    FeatureFlags.OWNER_LEAD_MEETING_SKILL,
  )

  useHeaderRight(
    !isNewAgent && watchedDirection === 'OUTBOUND' && outboundEnabled ? (
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={() => setShowSimulateModal(true)}
      >
        <PhoneOutgoing className="w-3.5 h-3.5 mr-1.5" />
        Simulate
      </Button>
    ) : null,
    [watchedDirection, isNewAgent, showSimulateModal, outboundEnabled],
  )

  useEffect(() => {
    if (!isNewAgent && currentCompany?.id && id) {
      form.reset(getDefaultValues(initialDirection))
      loadAgent()
    }
  }, [id, currentCompany?.id, isNewAgent])

  useEffect(() => {
    const fetchResources = async () => {
      if (!currentCompany?.id) return

      try {
        const knowledgeBasesPromise = isSuperAdmin
          ? knowledgeApiClient.getKnowledgeBasesForCompany(currentCompany.id)
          : knowledgeApiClient.getKnowledgeBases()
        const workflowsPromise = isSuperAdmin
          ? workflowsApiClient.getAllAdminHandoffWorkflowsForCompany(
              currentCompany.id,
            )
          : workflowsApiClient.getHandoffWorkflows()
        const triggersPromise = isSuperAdmin
          ? triggersApiClient.getTriggersForCompanyByAdmin(currentCompany.id)
          : triggersApiClient.getTriggers()
        const scenariosPromise = inboundMultiAgentEnabled
          ? isSuperAdmin
            ? scenariosApiClient.getScenariosForCompanyByAdmin(
                currentCompany.id,
              )
            : scenariosApiClient.getScenarios()
          : null
        // For new agents, load all agents here (loadAgent won't run)
        const agentsPromise =
          isNewAgent && inboundMultiAgentEnabled
            ? isSuperAdmin
              ? agentsAdminApiClient.getAgentsForCompany(currentCompany.id)
              : agentsApiClient.getCompanyAgents()
            : null
        const integrationsPromise = isSuperAdmin
          ? integrationsClient.getAllAdminIntegrationsStatus(currentCompany.id)
          : integrationsClient.getAllIntegrationsStatus()
        const [
          fetchedKnowledgeBases,
          fetchedWorkflows,
          fetchedTriggers,
          fetchedScenarios,
          fetchedAgents,
          fetchedIntegrations,
        ] = await Promise.all([
          knowledgeBasesPromise,
          workflowsPromise,
          triggersPromise,
          scenariosPromise,
          agentsPromise,
          integrationsPromise,
        ])
        setKnowledgeBases(fetchedKnowledgeBases || [])
        setWorkflows(fetchedWorkflows || [])
        setTriggers(fetchedTriggers || [])
        setScenarios(fetchedScenarios || [])
        if (fetchedAgents)
          setAllAgents(Array.isArray(fetchedAgents) ? fetchedAgents : [])
        setConnectedIntegrationTypes(
          (fetchedIntegrations || []).filter(i => i.connected).map(i => i.type),
        )
      } catch (error) {
        console.error('Error fetching knowledge bases and workflows:', error)
        toast.error('Failed to load knowledge bases and workflows')
        setKnowledgeBases([])
        setWorkflows([])
        setTriggers([])
        setScenarios([])
      }
    }

    fetchResources()
  }, [currentCompany?.id, isSuperAdmin])

  // Pre-select trigger when arriving from /triggers via
  // `?triggerEventName=<slug>` — mirrors InboundTriggerCard.onSelect.
  // Runs once after triggers load on the new-agent route.
  const presetTriggerApplied = useRef(false)
  useEffect(() => {
    if (!isNewAgent) return
    if (!presetTriggerEventName) return
    if (presetTriggerApplied.current) return
    if (triggers.length === 0) return
    const match = triggers.find(t => t.eventName === presetTriggerEventName)
    if (!match) return
    if (match.type === 'SCENARIO') {
      form.setValue('triggerType', 'SCENARIO', { shouldDirty: true })
      form.setValue('scenarioId', match.scenarioId ?? match.id, {
        shouldDirty: true,
      })
      form.setValue('triggerId', null, { shouldDirty: true })
      form.setValue('triggerEventName', null, { shouldDirty: true })
    } else if (match.triggerId) {
      // Custom Trigger row.
      form.setValue('triggerType', undefined, { shouldDirty: true })
      form.setValue('scenarioId', null, { shouldDirty: true })
      form.setValue('triggerId', match.triggerId, { shouldDirty: true })
      form.setValue('triggerEventName', match.eventName ?? null, {
        shouldDirty: true,
      })
    } else if (match.eventName) {
      // Built-in webhook (Lead / Collections).
      const mapped = WEBHOOK_EVENT_TO_TRIGGER[match.eventName]
      if (mapped) {
        form.setValue('triggerType', mapped, { shouldDirty: true })
        form.setValue('scenarioId', null, { shouldDirty: true })
        form.setValue('triggerId', null, { shouldDirty: true })
        form.setValue('triggerEventName', null, { shouldDirty: true })
      }
    }
    presetTriggerApplied.current = true
  }, [isNewAgent, presetTriggerEventName, triggers, form])

  // New agents: call intake gets all KBs and workflows pre-selected,
  // scenario starts empty. Existing agents: use whatever is saved.
  useEffect(() => {
    if (!isNewAgent) return
    const isScenarioAgent = watchedTriggerType === 'SCENARIO'
    if (workflows.length > 0) {
      form.setValue(
        'workflowIds',
        isScenarioAgent ? [] : workflows.map(wf => wf.id),
      )
    }
    if (knowledgeBases.length > 0) {
      form.setValue(
        'knowledgeBaseIds',
        isScenarioAgent ? [] : knowledgeBases.map(kb => kb.id),
      )
    }
  }, [watchedTriggerType, workflows, knowledgeBases, isNewAgent])

  const loadAgent = async () => {
    if (!currentCompany?.id || !id || isNewAgent) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const agents = isSuperAdmin
        ? await agentsAdminApiClient.getAgentsForCompany(currentCompany.id)
        : await agentsApiClient.getCompanyAgents()

      setAllAgents(Array.isArray(agents) ? agents : [])
      const existingAgent = agents.find(a => a.id === id)

      if (existingAgent) {
        setAgent(existingAgent)
        const existingDirection: AgentDirection =
          existingAgent.direction || 'INBOUND'

        const matchingVoice = VOICE_OPTIONS.find(
          opt => opt.id === existingAgent.voiceSettings?.voiceModelId,
        )

        form.reset({
          name: existingAgent.name,
          direction: existingDirection,
          channels: existingAgent.channels ?? ['CALL'],
          disallowedTopics:
            existingAgent.voiceSettings?.disallowedTopics ??
            VOICE_SETTINGS_DEFAULTS.disallowedTopics ??
            '',
          greeting: existingAgent.voiceSettings?.greeting || '',
          prompt: existingAgent.voiceSettings?.prompt || '',
          transferInstructions:
            existingAgent.voiceSettings?.transferInstructions || '',
          tone: existingAgent.voiceSettings?.tone || ['friendly'],
          voiceModelId: matchingVoice?.id,
          enableTypingInBackground:
            existingAgent.voiceSettings?.enableTypingInBackground ??
            VOICE_SETTINGS_DEFAULTS.enableTypingInBackground,
          smsFollowUpText: existingAgent.voiceSettings?.smsFollowUpText || '',
          isSmsFollowUpEnabled:
            existingAgent.voiceSettings?.isSmsFollowUpEnabled ??
            existingDirection === 'INBOUND',
          declinedMessage: existingAgent.voiceSettings?.declinedMessage || '',
          voicemailMessage: existingAgent.voiceSettings?.voicemailMessage || '',
          leaveVoicemailEnabled:
            existingAgent.voiceSettings?.leaveVoicemailEnabled ?? true,
          staffConnectionMethod:
            existingAgent.voiceSettings?.staffConnectionMethod ?? undefined,
          preserveOriginalCallerId:
            existingAgent.voiceSettings?.preserveOriginalCallerId ?? false,
          emailSubject: existingAgent.emailSettings?.subject || '',
          scenarioId: existingAgent.agentTriggers?.[0]?.scenarioId ?? null,
          triggerType: resolveFormTriggerType(existingAgent),
          triggerId: existingAgent.agentTriggers?.[0]?.triggerId ?? null,
          triggerEventName: existingAgent.agentTriggers?.[0]?.triggerId
            ? (existingAgent.agentTriggers?.[0]?.eventName ?? null)
            : null,
          knowledgeBaseIds: existingAgent.knowledgeBaseIds ?? [],
          workflowIds: existingAgent.workflowIds ?? [],
          dialOutSettings: {
            mode: existingAgent.dialOutSettings?.mode ?? null,
            actionGroupIds: existingAgent.dialOutSettings?.actionGroupIds ?? [],
            variableName: existingAgent.dialOutSettings?.variableName ?? null,
            intent:
              existingAgent.dialOutSettings?.intent ?? DIAL_OUT_INTENT_DEFAULT,
          },
          enabledTools: existingAgent.voiceSettings?.enabledTools ?? [],
          intakeForm: intakeFormValuesFromConfig(
            existingAgent.intakeFormConfig,
          ),
          skills: (existingAgent.skills ?? []).map(s => ({
            type: s.type,
            params: s.params ?? null,
          })),
        })

        // Mark dirty if legacy null triggerType needs migration to INBOUND_CALL
        if (
          inboundMultiAgentEnabled &&
          existingDirection === 'INBOUND' &&
          !existingAgent.voiceSettings?.triggerType
        ) {
          form.setValue('triggerType', 'INBOUND_CALL', { shouldDirty: true })
        }
      } else {
        toast.error('Agent not found')
        navigate('/agents')
      }
    } catch (error) {
      console.error('Error loading agent:', error)
      toast.error('Failed to load agent')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No current company selected')
      return false
    }

    try {
      setSaving(true)

      const direction: AgentDirection =
        values.direction ?? (typeParam === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND')
      const { triggerType: apiTriggerType, agentTrigger } =
        normalizeTriggerForApi(values, direction)
      const isReceptionist =
        direction === 'INBOUND' && apiTriggerType === 'INBOUND_CALL'
      // Receptionist supports all channels; all other agents use single-select
      const channels: AgentChannel[] = isReceptionist
        ? ['CALL', 'EMAIL', 'SMS']
        : values.channels

      const detectedVariables = extractVariablesFromTexts(
        values.greeting,
        values.prompt,
        values.transferInstructions,
        values.disallowedTopics,
        values.declinedMessage,
        values.smsFollowUpText,
        values.emailSubject,
      )
      const variables = detectedVariables
      const hasMetadataToSave = variables.length > 0 || direction === 'OUTBOUND'

      // Track if this save will replace the current default agent
      const isReplacingDefault =
        direction === 'INBOUND' &&
        apiTriggerType === 'INBOUND_CALL' &&
        currentDefaultAgentName !== null

      const existingMetadata = (agent?.metadata ?? {}) as Record<
        string,
        unknown
      >
      const baseMetadata = existingMetadata
      const intakeSlots = values.intakeForm?.slots ?? []

      const requestData = {
        name: values.name,
        direction,
        channels,
        ...(hasMetadataToSave && {
          metadata: {
            ...baseMetadata,
            ...(variables.length > 0 && {
              variables,
            }),
          },
        }),
        voiceSettings: {
          tone: values.tone ?? ['friendly'],
          disallowedTopics: values.disallowedTopics?.trim() || undefined,
          greeting: values.greeting || undefined,
          // Sent for all directions — inbound scenario agents need a prompt for the agent switcher.
          prompt: values.prompt || undefined,
          voiceModelId: values.voiceModelId || undefined,
          enableTypingInBackground: values.enableTypingInBackground ?? false,
          transferInstructions: values.transferInstructions || undefined,
          // An emptied field must be sent as null (not undefined) so the
          // update DTO actually clears the stored text instead of leaving
          // the previous custom message in place. Create has no "clear"
          // concept, so omit the field entirely (undefined) there.
          smsFollowUpText:
            values.smsFollowUpText?.trim() || (agent ? null : undefined),
          isSmsFollowUpEnabled: values.isSmsFollowUpEnabled ?? false,
          aiTransfersEnabled: values.staffConnectionMethod === 'AI_TRANSFER',
          declinedMessage: values.declinedMessage || undefined,
          voicemailMessage: values.voicemailMessage || undefined,
          leaveVoicemailEnabled: values.leaveVoicemailEnabled ?? true,
          staffConnectionMethod: values.staffConnectionMethod ?? null,
          preserveOriginalCallerId: values.preserveOriginalCallerId ?? false,
          enabledTools: values.enabledTools ?? [],
          triggerType: apiTriggerType,
        },
        intakeFormConfig:
          isReceptionist && values.intakeForm?.enabled && intakeSlots.length > 0
            ? { intakeForm: intakeSlots }
            : null,
        ...(channels.includes('EMAIL') && {
          emailSettings: {
            subject: values.emailSubject || undefined,
          },
        }),
        knowledgeBaseIds: values.knowledgeBaseIds ?? [],
        workflowIds: values.workflowIds ?? [],
        skills: values.skills ?? [],
        agentTrigger,
        dialOutSettings: buildDialOutSettingsPayload(
          values.dialOutSettings,
          direction,
          channels,
        ),
      }

      if (agent) {
        const updated = isSuperAdmin
          ? await agentsAdminApiClient.updateAgentForCompany(
              agent.id,
              requestData,
            )
          : await agentsApiClient.updateAgent(agent.id, requestData)
        setAgent(updated)
        toast.success('Agent updated successfully', {
          description: (
            <Link to={ROUTE_REGISTRY.AGENTS.path} className="underline">
              Back to agents
            </Link>
          ),
        })
        if (isReplacingDefault) {
          toast.info(
            `"${currentDefaultAgentName}" was deactivated — this agent is now the receptionist`,
          )
        }
      } else {
        const created = isSuperAdmin
          ? await agentsAdminApiClient.createAgentForCompany({
              ...requestData,
              companyId: currentCompany.id,
            })
          : await agentsApiClient.createAgent(requestData)

        if (created) {
          setAgent(created)
          toast.success('Agent created successfully')
          if (isReplacingDefault) {
            toast.info(
              `"${currentDefaultAgentName}" was deactivated — this agent is now the receptionist`,
            )
          }
          navigate(ROUTE_REGISTRY.AGENTS.path, { replace: true })
        } else {
          toast.error('Failed to create agent - no response')
          return false
        }
      }
      return true
    } catch (error) {
      console.error('Error saving agent:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save agent'
      toast.error(errorMessage)
      return false
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 pt-20 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    )
  }

  return (
    <div className="p-6 pt-20">
      <FloatingActionIslandProvider>
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0 pb-24">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-8 max-w-2xl">
                  {((directionForDisplay === 'INBOUND' &&
                    inboundMultiAgentEnabled) ||
                    (directionForDisplay === 'OUTBOUND' &&
                      triggers.length > 0)) && (
                    <TriggerRow
                      control={form.control}
                      setValue={form.setValue}
                      direction={directionForDisplay}
                      scenarios={scenarios}
                      triggers={triggers}
                      usedScenarioIds={usedScenarioIds}
                      usedTriggerEventNames={usedTriggerEventNames}
                      hasDefaultAgent={currentDefaultAgentName !== null}
                      currentDefaultAgentName={currentDefaultAgentName}
                    />
                  )}

                  <div className="relative">
                    <AgentCard
                      control={form.control}
                      directionForDisplay={directionForDisplay}
                      isReceptionist={isReceptionist}
                      triggerVariables={selectedTriggerVariables}
                    />
                    <div className="absolute top-4 right-4">
                      <Button
                        type="button"
                        variant={variablesPanelOpen ? 'secondary' : 'outline'}
                        size="icon"
                        onClick={() => setVariablesPanelOpen(prev => !prev)}
                        aria-label="Toggle variables panel"
                      >
                        <Braces className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <ChannelSettingsCard
                    control={form.control}
                    channels={watchedChannels ?? ['CALL']}
                    directionForDisplay={directionForDisplay}
                  />

                  {isDialOutAgent({
                    direction: watchedDirection,
                    channels: watchedChannels,
                  }) && (
                    <DialOutSettingsCard
                      control={form.control}
                      workflows={workflows}
                      triggerVariables={selectedTriggerVariables}
                    />
                  )}

                  {watchedChannels?.includes('CALL') && (
                    <SmsFollowUpCard
                      control={form.control}
                      watch={form.watch}
                      smsFollowUpInputRef={smsFollowUpInputRef}
                      direction={directionForDisplay}
                    />
                  )}

                  <ResourcesCard
                    control={form.control}
                    knowledgeBases={knowledgeBases}
                    workflows={workflows}
                    onNavigate={path => navigate(path)}
                  />

                  {watchedChannels?.includes('CALL') && (
                    <ToolsCard
                      control={form.control}
                      watch={form.watch}
                      setValue={form.setValue}
                      direction={directionForDisplay}
                      channels={watchedChannels ?? ['CALL']}
                      connectedIntegrationTypes={connectedIntegrationTypes}
                      ownerLeadMeetingSkillEnabled={
                        ownerLeadMeetingSkillEnabled
                      }
                    />
                  )}

                  {isReceptionist && (
                    <IntakeFormCard
                      control={form.control}
                      setValue={form.setValue}
                    />
                  )}

                  {watchedChannels?.includes('CALL') && (
                    <AgentAdvancedSettingsCard
                      control={form.control}
                      watch={form.watch}
                      declinedMessageInputRef={declinedMessageInputRef}
                      direction={watchedDirection}
                    />
                  )}
                </div>

                <AgentDetailSaveIsland
                  form={form}
                  onSubmit={onSubmit}
                  isNewAgent={isNewAgent}
                  agent={agent}
                  saving={saving}
                />
              </form>
            </Form>
          </div>
          <VariablesSidePanel
            triggerType={watchedTriggerType}
            isOpen={variablesPanelOpen}
            onClose={() => setVariablesPanelOpen(false)}
            channels={watchedChannels}
            resolvedScenarioType={
              watchedTriggerType === 'SCENARIO'
                ? (scenarios.find(s => s.id === watchedScenarioId)?.type ??
                  undefined)
                : undefined
            }
            customVariables={selectedTriggerVariables}
          />
        </div>

        <FloatingActionIsland />

        {!isNewAgent && agent && currentCompany && (
          <SimulateCallModal
            open={showSimulateModal}
            onOpenChange={setShowSimulateModal}
            agentId={agent.id}
            companyId={currentCompany.id}
            companyPhone={currentCompany.phone || ''}
            triggerType={agent.agentTriggers?.[0]?.type ?? triggers?.[0]?.type}
            agentVariables={getScenarioVariableNames(
              (agent.metadata as Record<string, unknown>)?.scenario as
                | string
                | undefined,
              agent.agentTriggers?.[0]?.type ?? triggers?.[0]?.type,
            )}
          />
        )}
      </FloatingActionIslandProvider>
    </div>
  )
}
